import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RootLayout } from '@/components/RootLayout';
import { ProtectedRoute, PublicRoute } from '@/components/route-guards';
import { RehydrationProvider } from '@/components/RehydrationProvider';
import { AuthScreen } from '@/screens/AuthScreen';
import { HomeHubScreen } from '@/screens/HomeHubScreen';
import { QuickMatchScreen } from '@/screens/QuickMatchScreen';
import { LeaderboardScreen } from '@/screens/LeaderboardScreen';
import { ProfileScreen } from '@/screens/ProfileScreen';
import { GameScreen } from '@/screens/GameScreen';

/**
 * App router tree with strict rehydration pipeline.
 *
 * Topology:
 *  - Public branch:        /auth  -> AuthScreen (PublicRoute guards hydration)
 *  - Protected branch:     RootLayout wraps /, /quick-match, /leaderboard, /profile, /game/:matchId
 *                          Protected by ProtectedRoute AND RehydrationProvider
 *
 * The RehydrationProvider blocks rendering of protected routes until:
 * 1. Token refreshed via httpOnly cookie
 * 2. Socket.io connected
 * 3. If active match exists: reconnect:attempt sent + game:state received
 */
function App() {
  return (
    <ErrorBoundary>
      <Routes>
      {/* Public routes - no rehydration blocking needed */}
      <Route
        path="/auth"
        element={
          <PublicRoute>
            <AuthScreen />
          </PublicRoute>
        }
      />
      {/* Legacy auth path aliases -> /auth */}
      <Route path="/login" element={<Navigate to="/auth" replace />} />
      <Route path="/register" element={<Navigate to="/auth" replace />} />

      {/* Protected routes with rehydration blocking */}
      <Route
        element={
          <ProtectedRoute>
            {/* RehydrationProvider blocks until pipeline complete */}
            <RehydrationProvider>
              <RootLayout />
            </RehydrationProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<HomeHubScreen />} />
        <Route path="/quick-match" element={<QuickMatchScreen />} />
        <Route path="/leaderboard" element={<LeaderboardScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/game/:matchId" element={<GameScreen />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;